import React, { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"

import { ClientOnly, Layout } from "../components/common"
import { MetaData } from "../components/common/meta"
import FirebaseProvider from "../components/idealline/Firebase"
import SponsorshipRequestForm from "../components/sponsorship/SponsorshipRequestForm"
import { sponsorshipPlacementFromLocation, sponsorshipRequestPath } from "../utils/sponsorshipRequest"
import "../styles/sponsorship.css"

const PackageCard = ({ actionLabel, description, name, onSelect, price, scope }) => (
  <article className="sponsor-package">
    <div className="sponsor-package__topline">
      <span>{scope}</span>
    </div>
    <h2>{name}</h2>
    <p className="sponsor-package__price"><strong>€{price}</strong><span>/ month</span></p>
    <p>{description}</p>
    <button className="sponsor-package__action" onClick={onSelect} type="button">
      {actionLabel}
    </button>
  </article>
)

const SponsorshipPage = ({ location }) => {
  const [placement, setPlacement] = useState(() => sponsorshipPlacementFromLocation(location))
  const requestSection = useRef(null)
  const requestFocusPending = useRef(false)
  const requestFocusFrame = useRef(null)

  const cancelRequestFocus = useCallback(() => {
    requestFocusPending.current = false
    if (requestFocusFrame.current !== null) {
      window.cancelAnimationFrame(requestFocusFrame.current)
      requestFocusFrame.current = null
    }
  }, [])

  const focusRequestTarget = useCallback(() => {
    const target = requestSection.current?.querySelector("a[href], input:not([disabled]), textarea:not([disabled]), button:not([disabled])")
    if (!target) return false
    target.focus({ preventScroll: true })
    return true
  }, [])

  const handleRequestActionReady = useCallback(() => {
    if (!requestFocusPending.current || requestFocusFrame.current !== null) return
    const active = document.activeElement
    if (active !== document.body && active !== requestSection.current) {
      requestFocusPending.current = false
      return
    }
    if (focusRequestTarget()) requestFocusPending.current = false
  }, [focusRequestTarget])

  const focusSponsorshipAction = useCallback(() => {
    cancelRequestFocus()
    requestFocusPending.current = true
    requestFocusFrame.current = window.requestAnimationFrame(() => {
      requestFocusFrame.current = null
      const section = requestSection.current
      if (!section) return
      section.scrollIntoView({ behavior: "smooth", block: "start" })
      if (focusRequestTarget()) requestFocusPending.current = false
      else section.focus({ preventScroll: true })
    })
  }, [cancelRequestFocus, focusRequestTarget])

  useEffect(() => {
    const syncPlacement = () => setPlacement(sponsorshipPlacementFromLocation(window.location))
    window.addEventListener("popstate", syncPlacement)
    return () => window.removeEventListener("popstate", syncPlacement)
  }, [])

  useEffect(() => () => cancelRequestFocus(), [cancelRequestFocus])

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash === "#sponsorship-request") {
      focusSponsorshipAction()
    }
  }, [focusSponsorshipAction])

  const selectPackage = packageKey => {
    const path = sponsorshipRequestPath({ packageKey })
    window.history.pushState({}, "", `${path}#sponsorship-request`)
    setPlacement(sponsorshipPlacementFromLocation(window.location))
    focusSponsorshipAction()
  }

  return (
    <>
      <MetaData
        location={location}
        title="Sponsor Open Racer"
        description="Partner with Open Racer site-wide or across the global track-discovery experience."
      />
      <Layout>
        <main className="sponsor-page">
          <header className="sponsor-hero">
            <div className="sponsor-hero__copy">
              <p className="sponsor-kicker">Commercial partnerships</p>
              <h1>Meet racers where they plan the next lap.</h1>
              <p className="sponsor-hero__lead">Sponsor Open Racer site-wide or focus your presence on global track discovery. Every placement is reviewed for relevance and fit.</p>
            </div>
            <aside className="sponsor-hero__note">
              <span>Version one</span>
              <p>Submit interest now. No payment is taken, and no placement becomes active until approval and separate subscription setup.</p>
            </aside>
          </header>

          <section className="sponsor-packages" aria-labelledby="sponsor-packages-title" id="sponsorship-options">
            <div className="sponsor-section-heading">
              <p className="sponsor-kicker">Launch pricing</p>
              <h2 id="sponsor-packages-title">Two focused ways to partner</h2>
              <p>Monthly pricing begins only after approval. Availability, placement, and timing are subject to review.</p>
            </div>
            <div className="sponsor-package-grid">
              <PackageCard
                actionLabel="Sponsor site-wide"
                description="A premium partnership proposal for visibility across Open Racer's site-wide experience."
                name="Open Racer Partner"
                onSelect={() => selectPackage("site")}
                price="499"
                scope="Site-wide"
              />
              <PackageCard
                actionLabel="Sponsor track discovery"
                description="A focused presence across homepage search, the track directory, nearby-track results, and global discovery surfaces."
                name="Track Discovery Partner"
                onSelect={() => selectPackage("discovery")}
                price="199"
                scope="Track discovery"
              />
            </div>
          </section>

          <section className="sponsor-principles" aria-label="Partnership principles">
            <div><strong>Relevant</strong><span>Racing, automotive, equipment, events, and services that genuinely help drivers.</span></div>
            <div><strong>Transparent</strong><span>Sponsored placement will be identified clearly and kept separate from organic results.</span></div>
            <div><strong>Measured</strong><span>This first release validates buyer interest before billing or automated activation is introduced.</span></div>
          </section>

          <section
            aria-label="Sponsorship request"
            className="sponsor-request-action"
            id="sponsorship-request"
            ref={requestSection}
            tabIndex="-1"
          >
            <ClientOnly>
              <FirebaseProvider>
                <SponsorshipRequestForm onActionReady={handleRequestActionReady} placement={placement} />
              </FirebaseProvider>
            </ClientOnly>
          </section>
        </main>
      </Layout>
    </>
  )
}

PackageCard.propTypes = {
  actionLabel: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  price: PropTypes.string.isRequired,
  scope: PropTypes.string.isRequired,
}

SponsorshipPage.propTypes = {
  location: PropTypes.shape({ pathname: PropTypes.string, search: PropTypes.string }).isRequired,
}

export default SponsorshipPage
